import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { MapPin, Clock, Award, ExternalLink } from "lucide-react"

interface SchoolCardProps {
  school: {
    id: string
    name: string
    description: string
    location: string
    image_url: string | null
    specialties: string[]
    certification: string
    duration: string
    website_url: string | null
  }
}

export function SchoolCard({ school }: SchoolCardProps) {
  return (
    <Card className="overflow-hidden h-full transition-all duration-300 hover:shadow-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 group rounded-xl">
      <div className="relative h-52 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent z-10" />
        <img
          src={school.image_url || '/placeholder.svg'}
          alt={school.name}
          className="w-full h-full object-cover transform transition-transform duration-500 group-hover:scale-110"
        />
        <div className="absolute bottom-0 left-0 right-0 p-4 z-20">
          <h3 className="text-xl font-bold tracking-tight text-white mb-1">{school.name}</h3>
          <div className="flex items-center text-sm text-white/90">
            <MapPin className="mr-1 h-4 w-4 text-purple-200" />
            {school.location}
          </div>
        </div>
      </div>
      
      <CardContent className="p-6 space-y-6 bg-white dark:bg-slate-800">
        <p className="text-sm text-slate-700 dark:text-slate-300 line-clamp-3">{school.description}</p>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-2 p-3 rounded-lg bg-slate-50 dark:bg-slate-700/30">
            <Award className="h-5 w-5 text-purple-600 dark:text-purple-400 mb-1" />
            <span className="text-xs font-medium text-slate-500 dark:text-slate-400">Certification</span>
            <span className="text-sm font-semibold text-slate-900 dark:text-white">{school.certification}</span>
          </div>
          
          <div className="flex flex-col gap-2 p-3 rounded-lg bg-slate-50 dark:bg-slate-700/30">
            <Clock className="h-5 w-5 text-purple-600 dark:text-purple-400 mb-1" />
            <span className="text-xs font-medium text-slate-500 dark:text-slate-400">Duration</span>
            <span className="text-sm font-semibold text-slate-900 dark:text-white">{school.duration}</span>
          </div>
        </div>

        <div className="pt-2">
          <div className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">Specialties</div>
          <div className="flex flex-wrap gap-2">
            {school.specialties.map((specialty) => (
              <Badge 
                key={specialty} 
                variant="secondary"
                className="bg-purple-50 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-900/60 rounded-full px-3 py-1 text-xs"
              >
                {specialty}
              </Badge>
            ))}
          </div>
        </div>

        {school.website_url && (
          <Button 
            className="w-full group bg-purple-600 hover:bg-purple-700 text-white dark:bg-purple-700 dark:hover:bg-purple-600 rounded-full font-medium shadow-sm transition-all duration-300 mt-2"
            onClick={() => window.open(school.website_url!, '_blank')}
          >
            Learn More
            <ExternalLink className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Button>
        )}
      </CardContent>
    </Card>
  )
}
